import { useState } from "react";

import { LABEEB_AUDIT_DOMAINS } from "../../api/labeeb";
import { AUDIT_DOMAINS } from "../../api/mcp";
import { AuditEventTable } from "../../components/audit/AuditEventTable";
import { LoadMore } from "../../components/LoadMore";
import { LoadFailed, ResourceView } from "../../components/states";
import { Empty, FilterBar, Notice, Page, Segmented, SelectField } from "../../components/ui";
import type { AppConfig } from "../../config";
import { Input } from "../../f7";
import { useAuditFeed, type AnyAuditDomain, type AuditProduct } from "../../hooks/useAuditFeed";
import { AuditCoverageNote } from "../../components/labeeb/DeploymentFacts";
import { ProductTag } from "../../products/PerProduct";

/** The domains each product's audit table keys on. They are NOT the same set. */
export function domainsFor(product: AuditProduct): readonly string[] {
  return product === "labeeb" ? LABEEB_AUDIT_DOMAINS : AUDIT_DOMAINS;
}

/**
 * Who changed what, through this console.
 *
 * NOT merchant tool-call audit. Different table, different question, and the
 * API reference says explicitly never to merge the two into one panel — an
 * operator asking "who capped this tool" and one asking "what did this
 * merchant's assistant do" are looking for opposite things.
 *
 * This is also the ONLY durable record of two events. A blocklist row is
 * deleted on unblock, so nothing else remembers that someone was ever blocked
 * or who lifted it; a cap's `updated_by` stamp is overwritten on every change,
 * so nothing else remembers who set the previous value.
 */
export function Audit({ config }: { config: AppConfig }) {
  const [product, setProduct] = useState<AuditProduct>("mcp");
  const [domain, setDomain] = useState<AnyAuditDomain>("");
  const [days, setDays] = useState(7);
  const { state, reload, loadMore, loadingMore, moreError } = useAuditFeed(
    config,
    domain,
    product,
    days,
  );
  const windowLabel = days === 1 ? "1 day" : `${days} days`;

  // The domain vocabularies do not overlap fully — `settings` is MCP's,
  // `flags` and `config` are Labeeb's. Carrying a filter across the switch
  // would send the other product a domain it answers 422 to, which reads as a
  // broken page rather than a filter that does not apply.
  function switchProduct(next: AuditProduct) {
    setProduct(next);
    setDomain("");
  }

  return (
    <Page
      title="Audit log"
      subtitle="Admin changes made through this console — not merchant tool calls."
    >

      {/* WHOSE LOG. Each product's admin API keeps its own audit table, so
          there is no single feed to show — the two paginate independently and
          key on different domains. This page used to read MCP's and carry a
          notice apologising for the other half; the switch is that notice
          turned into the feature. */}

      {/* One filter row, not a flex row wrapping another flex row. FilterBar
          already lays its children out on one line with the design's gap. */}
      <FilterBar>
        <Segmented
          value={product}
          onChange={switchProduct}
          label="Which product's audit log"
          options={[
            { value: "mcp", label: "MCP" },
            { value: "labeeb", label: "Labeeb" },
          ]}
        />
        <SelectField
          value={domain}
          onChange={(v) => setDomain(v as AnyAuditDomain)}
          label="Filter by domain"
          width={220}
        >
          {/* Only MCP's fleet list is windowed. Labeeb's audit takes limit and
              cursor only, so claiming a window there would be untrue. */}
          <option value="">{product === "mcp" ? `Everything, last ${windowLabel}` : "Everything"}</option>
          {domainsFor(product).map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </SelectField>
        <ProductTag id={product} />
        {!domain && product === "mcp" && (
          <label className="text-xs text-slate-500 dark:text-slate-400">
            Days
            <div className="mt-1 w-24">
              <Input
                type="number"
                value={String(days)}
                aria-label="How many days"
                onChange={(e) => {
                  const n = Number(e.target.value);
                  if (!Number.isInteger(n)) return;
                  setDays(Math.min(90, Math.max(1, n)));
                }}
              />
            </div>
          </label>
        )}
        {!domain && product === "mcp" && (
          <span className="text-xs text-slate-500 dark:text-slate-400">
            The unfiltered view is windowed to {windowLabel}; a domain filter is not.
          </span>
        )}
      </FilterBar>

      {product === "labeeb" && <AuditCoverageNote config={config} />}

      {/* A 422 means the domain itself was rejected — "you asked for the wrong
          thing", never "nothing happened". Rendering it as an empty feed would
          report silence where there was a refusal. */}
      <ResourceView
        state={state}
        what="the audit log"
        onRetry={reload}
        notFound={<LoadFailed what="the audit log" message="Route not found on this gateway." />}
        invalid={(error) => (
          <Notice tone="amber" title="That domain does not exist">
            {error.message} Valid domains are {domainsFor(product).join(", ")}.
          </Notice>
        )}
      />

      {/* `Load more` lives OUTSIDE the has-rows branch. A DynamoDB page can
          come back empty while later pages still hold rows — the cursor, not
          the row count, is what says whether there is more — and gating the
          button on `events.length > 0` stranded the feed on "No admin changes"
          with unread pages behind it. */}
      {state.kind === "ready" && (
        <>
          {state.data.rows.length === 0 ? (
            <Empty>
              {domain
                ? `No ${domain} changes recorded.`
                : product === "mcp"
                  ? `No admin changes in the last ${windowLabel}.`
                  : "No admin changes recorded."}
            </Empty>
          ) : (
            <AuditEventTable rows={state.data.rows} product={product} />
          )}

          {(state.data.rows.length > 0 || state.data.next !== null) && (
            <LoadMore
              shown={state.data.rows.length}
              noun="changes"
              hasMore={state.data.next !== null}
              loading={loadingMore}
              error={moreError}
              onMore={loadMore}
            />
          )}
        </>
      )}
    </Page>
  );
}

export { targetLink } from "../../components/audit/targetLink";
